import type { Office } from '../../../components/firms/firm/mapper'

/**
 * Formats an office address into a standardized string representation
 * @param office - The office object containing address information (street, city, postalCode)
 * @returns A formatted string representing the office address, or an empty string if office is null/undefined
 * @example
 * const office = {
 *   street: '123 Rue de la Paix',
 *   city: 'Paris',
 *   postalCode: '75001'
 * };
 * const address = getOfficeAddress(office);
 * // Result: "123 Rue de la Paix, Paris (75)"
 * 
 * @example
 * const emptyOffice = null;
 * const address = getOfficeAddress(emptyOffice);
 * // Result: ""
 */
export function getOfficeAddress(office: Office) {
  if (!office) {
    return ''
  }
  const strings = []
  const { street, city, postalCode } = office

  if (street) {
    strings.push(`${street},`)
  }

  if (city) {
    strings.push(city)
  }

  if (postalCode) {
    strings.push(`(${postalCode.slice(0, 2)})`)
  }

  return strings.join(' ')
}

/**
 * Sorts an array of offices by their department number (first two digits of postal code)
 * @param offices - Array of office objects to be sorted
 * @returns A new array of offices sorted by department number in ascending order
 * @example
 * const offices = [
 *   { postalCode: '75001', name: 'Paris Office' },
 *   { postalCode: '69001', name: 'Lyon Office' },
 *   { postalCode: '13001', name: 'Marseille Office' }
 * ];
 * const sortedOffices = sortOfficeAddressesByDepartementNb(offices);
 * // Result: [
 *   { postalCode: '13001', name: 'Marseille Office' },
 *   { postalCode: '69001', name: 'Lyon Office' },
 *   { postalCode: '75001', name: 'Paris Office' }
 * ]
 */
export function sortOfficeAddressesByDepartementNb(offices: readonly Office[]) {
  if (!offices) {
    return []
  }
  return [...offices].sort((a, b) => {
    const aDepartementNb = a?.postalCode?.slice(0, 2) ?? ''
    const bDepartementNb = b?.postalCode?.slice(0, 2) ?? ''
    return aDepartementNb.localeCompare(bDepartementNb)
  })
}
